import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import HeroVideo from "@/components/HeroVideo";

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.2, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [2.5, -2.5]), { stiffness: 60, damping: 20 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-2.5, 2.5]), { stiffness: 60, damping: 20 });

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="top"
      onMouseMove={onMouseMove}
      className="relative overflow-hidden pt-36 pb-20 lg:pt-44 lg:pb-28"
      data-testid="hero-section"
    >
      <div className="pointer-events-none absolute left-1/2 top-1/3 -z-10 h-[60vh] w-[70vw] -translate-x-1/2 rounded-full bg-white/[0.04] blur-[120px]" />

      <div className="mx-auto max-w-5xl px-6 text-center lg:px-10">
        <div className="flex justify-center">
          <h1 className="font-serif font-light leading-[0.98] tracking-tight" data-testid="hero-headline">
            <MaskedLine delay={0.35} className="text-4xl sm:text-6xl lg:text-7xl">
              <span className="inline-flex items-center gap-4 sm:gap-6">
                Watch this first
                <ArrowDown className="h-[0.7em] w-[0.7em] shrink-0 text-neutral-400" strokeWidth={1.25} aria-hidden="true" />
              </span>
            </MaskedLine>
            <MaskedLine delay={0.5} className="text-4xl sm:text-6xl lg:text-7xl">
              <span className="italic text-neutral-400">then apply below.</span>
            </MaskedLine>
          </h1>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 60, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-14 lg:mt-16"
        >
          <HeroVideo rx={rx} ry={ry} />
        </motion.div>
      </div>
    </section>
  );
}
